/**
 * Purchasing document kinds (API route segments of docs/contracts/purchasing.md §4), their permission features, sources
 * of copy-from and the fields each kind uses.
 */
export const purchaseKinds = [
  "requisitions",
  "rfqs",
  "quotations",
  "orders",
  "contracts",
  "goods-receipts",
  "invoices",
  "return-requests",
  "returns",
  "debit-notes",
] as const;

export type PurchaseKind = (typeof purchaseKinds)[number];

/** Kind code of the API (`kind` of views and `sourceKind` of open lines). */
export const kindCode: Record<PurchaseKind, string> = {
  requisitions: "requisition",
  rfqs: "rfq",
  quotations: "quotation",
  orders: "order",
  contracts: "contract",
  "goods-receipts": "goodsReceipt",
  invoices: "invoice",
  "return-requests": "returnRequest",
  returns: "return",
  "debit-notes": "debitNote",
};

/** Translation key stem of a kind (`purch:kinds.<key>.*`) and of its navigation label. */
export const kindKey: Record<PurchaseKind, string> = {
  requisitions: "requisition",
  rfqs: "rfq",
  quotations: "quotation",
  orders: "order",
  contracts: "contract",
  "goods-receipts": "goodsReceipt",
  invoices: "invoice",
  "return-requests": "returnRequest",
  returns: "return",
  "debit-notes": "debitNote",
};

/** Catalog feature of each kind (legacy keys kept; SP17: create, approve and post are separate). */
const feature: Record<PurchaseKind, string> = {
  requisitions: "proc.procurment_request",
  rfqs: "proc.purchase_request",
  quotations: "purch.purchase_quotation",
  orders: "purch.purchase_order",
  contracts: "purch.purchase_contract",
  "goods-receipts": "purch.goods_receipt",
  invoices: "purch.purchase_accounting",
  "return-requests": "purch.purchase_return_request",
  returns: "purch.goods_return",
  "debit-notes": "purch.debit_note",
};

export type PurchaseAction =
  "view" | "create" | "update" | "delete" | "approve" | "post" | "reverse" | "close" | "cancel" | "print";

/** Catalog key of an action on a kind (e.g. `purch.purchase_order.post`). */
export function kindPermission(kind: PurchaseKind, action: PurchaseAction): string {
  return `${feature[kind]}.${action}`;
}

export const listPaths = {
  requisitions: "/purch/requisitions",
  rfqs: "/purch/rfqs",
  quotations: "/purch/quotations",
  orders: "/purch/orders",
  contracts: "/purch/contracts",
  "goods-receipts": "/purch/goods-receipts",
  invoices: "/purch/invoices",
  "return-requests": "/purch/return-requests",
  returns: "/purch/returns",
  "debit-notes": "/purch/debit-notes",
} as const satisfies Record<PurchaseKind, string>;

export const objectPaths = {
  requisitions: "/purch/requisitions/$documentId",
  rfqs: "/purch/rfqs/$documentId",
  quotations: "/purch/quotations/$documentId",
  orders: "/purch/orders/$documentId",
  contracts: "/purch/contracts/$documentId",
  "goods-receipts": "/purch/goods-receipts/$documentId",
  invoices: "/purch/invoices/$documentId",
  "return-requests": "/purch/return-requests/$documentId",
  returns: "/purch/returns/$documentId",
  "debit-notes": "/purch/debit-notes/$documentId",
} as const satisfies Record<PurchaseKind, string>;

export function isPurchaseKind(value: string): value is PurchaseKind {
  return (purchaseKinds as readonly string[]).includes(value);
}

/** What a kind does and which header / line fields it shows. */
export interface KindShape {
  /** Posting kinds post (number, stock, ledger); the others are released (number at release). */
  posting: boolean;
  /** Supplier on the header (required). */
  supplier: boolean;
  /** Lines carry prices. */
  prices: boolean;
  /** Header warehouse and stock lines (receipts, returns, direct invoices). */
  warehouse: boolean;
  /** Validity / closing date. */
  validity: boolean;
  /** Kinds whose open lines can be copied in (copy-from chain, SP9 / IV15). */
  sources: readonly PurchaseKind[];
  /** Released documents can be closed / cancelled. */
  closable: boolean;
  /** Posted documents can be reversed (D5). */
  reversible: boolean;
}

export const kindShape: Record<PurchaseKind, KindShape> = {
  requisitions: {
    posting: false,
    supplier: false,
    prices: false,
    warehouse: true,
    validity: false,
    sources: [],
    closable: true,
    reversible: false,
  },
  rfqs: {
    posting: false,
    supplier: false,
    prices: false,
    warehouse: false,
    validity: true,
    sources: ["requisitions"],
    closable: true,
    reversible: false,
  },
  quotations: {
    posting: false,
    supplier: true,
    prices: true,
    warehouse: false,
    validity: true,
    sources: ["rfqs", "requisitions"],
    closable: true,
    reversible: false,
  },
  orders: {
    posting: false,
    supplier: true,
    prices: true,
    warehouse: true,
    validity: false,
    sources: ["quotations", "requisitions", "contracts"],
    closable: true,
    reversible: false,
  },
  contracts: {
    posting: false,
    supplier: true,
    prices: true,
    warehouse: false,
    validity: true,
    sources: ["quotations"],
    closable: true,
    reversible: false,
  },
  "goods-receipts": {
    posting: true,
    supplier: true,
    prices: false,
    warehouse: true,
    validity: false,
    sources: ["orders", "contracts"],
    closable: false,
    reversible: true,
  },
  invoices: {
    posting: true,
    supplier: true,
    prices: true,
    warehouse: true,
    validity: false,
    sources: ["goods-receipts", "orders"],
    closable: false,
    reversible: true,
  },
  "return-requests": {
    posting: false,
    supplier: true,
    prices: true,
    warehouse: true,
    validity: false,
    sources: ["goods-receipts", "invoices"],
    closable: true,
    reversible: false,
  },
  returns: {
    posting: true,
    supplier: true,
    prices: false,
    warehouse: true,
    validity: false,
    sources: ["return-requests", "goods-receipts", "invoices"],
    closable: false,
    reversible: true,
  },
  "debit-notes": {
    posting: true,
    supplier: true,
    prices: true,
    warehouse: false,
    validity: false,
    sources: ["invoices"],
    closable: false,
    reversible: true,
  },
};

/** Report keys (`/purch/reports/{key}`) and their permission keys. */
export const reportKeys = [
  "payables-aging",
  "supplier-statement",
  "open-orders",
  "purchase-analysis",
  "match-exceptions",
  "price-history",
] as const;
export type ReportKey = (typeof reportKeys)[number];

export const reportPermission: Record<ReportKey, string> = {
  "payables-aging": "rpt.accounts_payable_aging.view",
  "supplier-statement": "rpt.statement_of_account.view",
  "open-orders": "rpt.open_purchase_orders.view",
  "purchase-analysis": "rpt.purchase_details.view",
  "match-exceptions": "rpt.match_exceptions.view",
  "price-history": "rpt.purchase_details.view",
};

export const reportKeyName: Record<ReportKey, string> = {
  "payables-aging": "payablesAging",
  "supplier-statement": "supplierStatement",
  "open-orders": "openOrders",
  "purchase-analysis": "purchaseAnalysis",
  "match-exceptions": "matchExceptions",
  "price-history": "priceHistory",
};

export function isReportKey(value: string): value is ReportKey {
  return (reportKeys as readonly string[]).includes(value);
}
